import { useTranslation } from "react-i18next";
import { PageHeader, PageTitle } from "../../../layout/portalPageLayout";
import { PageWrapper } from "../../../layout/portalPageLayout";
import { useRoutePath } from "../../../util/Routing";
import { FullWidthHorizontalSeparator } from "../../../layout/portalPageLayout";
import styled, { useTheme } from "styled-components/macro";
import useSWR, { useSWRInfinite } from "swr";
import type {
  DashboardOtherUpdate,
  DashboardProductUpdate,
  DashboardSummary,
  DashboardUpdate,
  OptionType,
  WithPagination,
} from "../../../types/types";
import { endpoints } from "../../../endpoints";
import { useIsActylisStorefront, useStoreState } from "../../../util/util";
import { DelayedSpinner } from "../../../components/DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../../components/Error";
import { SoftHeaderMedium } from "../../../components/Typography/Typography";
import { H3DarkText } from "../../../components/Typography/Typography";
import { H5 } from "../../../components/Typography/Typography";
import { match } from "ts-pattern";
import { screenSize } from "../../../theme";
import { useCallback, useState } from "react";
import { DashboardProductUpdates } from "./DashboardProductUpdates";
import { DashboardOtherUpdates } from "./DashboardOtherUpdates";
import { useAuthContext } from "../../../components/Auth";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";
import { PAGE_SIZE } from "./dashboard.util";
import { constructInfiniteScrollQuery } from "../../../util/paginatedInfiniteScrollQuery";
import { DisplayBroadcastMessages } from "../../admin/SellerAdmin/Broadcasts/DisplayBroadcastMessages";
import { HeaderLeft, HeaderRight } from "../../../components/Layout/Layout";
import type { SVGButtonProps } from "../../../components/Buttons/Buttons";
import { TertiaryButtonMedium } from "../../../components/Buttons/Buttons";
import { BellIcon } from "../../../components/Icons/Icons";
import { useHistory } from "react-router-dom";
import { spacingTokens } from "../../../designTokens";

const DashboardParentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${({ theme }) => theme.resolve("spacing_5xl")};
`;

const DashboardSummarySection = styled.section`
  width: 100%;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.resolve("spacing_3xl")};
`;

const DashboardSummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  border: ${({ theme }) =>
    `${theme.resolve("border_md")} solid ${theme.secondaryBorder}`};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  flex: 1;
`;

const DashboardUpdatesSection = styled.section`
  width: 100%;
  height: fit-content;
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.resolve("spacing_4xl")};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.resolve("spacing_4xl")};
  @media ${screenSize.mLarge} {
    width: 100%;
  }
  @media ${screenSize.medium} {
    flex-direction: column;
  }
`;

const TertiaryButtonMediumWithBellIcon = (
  props: SVGButtonProps
): JSX.Element => {
  const theme = useTheme();
  return (
    <TertiaryButtonMedium {...props}>
      <span
        data-for={props.datafor}
        data-tip={props.datatip}
        style={{
          display: "flex",
          alignItems: "center",
          gap: spacingTokens.spacing_xs,
        }}
      >
        <BellIcon width={20} height={20} fill={theme.primaryIconColor} />
        <span>{props.children}</span>
      </span>
    </TertiaryButtonMedium>
  );
};

export const Dashboard = () => {
  const user = useAuthenticatedUser();
  const [otherUpdates, setOtherUpdates] = useState<DashboardOtherUpdate[]>([]);
  const [productUpdates, setProductUpdates] = useState<
    DashboardProductUpdate[]
  >([]);
  const { storefront_id } = useStoreState();
  const { hasPermission, roleIsSellerAdmin } = useAuthContext();
  const { t } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const { adminPath } = useRoutePath();
  const isActylisStorefront = useIsActylisStorefront();

  const { data: dashboardSummary, error: dashboardSummaryError } =
    useSWR<DashboardSummary>(
      endpoints.v2_storefronts_id_dashboard_summary(storefront_id)
    );
  const isSummaryLoading = !dashboardSummary && !dashboardSummaryError;

  const dashboardSummaryArr: OptionType<keyof DashboardSummary>[] = [
    { label: t("Total Active Products"), value: "total_active_products" },
    {
      label: isActylisStorefront
        ? t("Total Active Assets (Including Documents)")
        : t("Total Active Assets"),
      value: "total_active_assets",
    },
    { label: t("Total Active Customers"), value: "total_active_customers" },
    { label: t("Total Active Users"), value: "total_active_users" },
  ];

  const date = (() => {
    const options = {
      weekday: "long",
      month: "long",
      day: "numeric",
    } as Intl.DateTimeFormatOptions;
    const locale = Intl.DateTimeFormat().resolvedOptions().locale ?? "en-US";
    return new Intl.DateTimeFormat(locale, options).format(new Date());
  })();

  const getGreeting = (() => {
    const hours = new Date().getHours();
    return hours < 12
      ? t("Good morning")
      : hours < 18
      ? t("Good afternoon")
      : t("Good evening");
  })();

  const permissionAssignment = useCallback(
    (
      status: "archived" | "deleted" | "created" | "updated",
      permission:
        | "assets"
        | "attributes"
        | "templates"
        | "products"
        | "groups"
        | "collections"
        | "lists"
        | "customers"
        | "suppliers"
        | "distributors"
    ) =>
      status === "archived" || status === "deleted"
        ? hasPermission(`delete_${permission}`)
        : hasPermission([`view_${permission}`, `modify_${permission}`]),
    [hasPermission]
  );

  const hasPermissionToView = useCallback(
    (
      type: DashboardUpdate["type"],
      status: "archived" | "deleted" | "created" | "updated"
    ) => {
      try {
        return match(type)
          .with("assets", () => permissionAssignment(status, "assets"))
          .with("attribute", () => permissionAssignment(status, "attributes"))
          .with("collection", () => permissionAssignment(status, "collections"))
          .with("group", () => permissionAssignment(status, "groups"))
          .with("list", () => permissionAssignment(status, "lists"))
          .with("product", () => permissionAssignment(status, "products"))
          .with("template", () => permissionAssignment(status, "templates"))
          .with("customer_template", () =>
            permissionAssignment(status, "customers")
          )
          .with("supplier_template", () =>
            permissionAssignment(status, "suppliers")
          )
          .with("distributor_template", () =>
            permissionAssignment(status, "distributors")
          )
          .exhaustive();
      } catch (error) {
        console.error(`Update type ${type} not expected`);
        return false;
      }
    },
    [permissionAssignment]
  );

  const {
    error: otherUpdatesError,
    setSize: setOtherUpdatesSize,
    size: otherUpdatesSize,
  } = useSWRInfinite<WithPagination<{ data: DashboardOtherUpdate[] }>>(
    (index, previousPageData) =>
      constructInfiniteScrollQuery({
        baseUrl:
          endpoints.v2_storefronts_id_dashboard_other_updates(storefront_id),
        query: "",
        previousPageData,
        index,
        pageSize: PAGE_SIZE,
      }),
    {
      onSuccess: (data) => {
        const updates = data.flatMap(({ data }) =>
          data.filter(({ type, update_data: { status } }) =>
            hasPermissionToView(type, status)
          )
        );
        setOtherUpdates(updates);
      },
    }
  );

  const {
    error: productUpdatesError,
    setSize: setProductUpdatesSize,
    size: productUpdatesSize,
  } = useSWRInfinite<WithPagination<{ data: DashboardProductUpdate[] }>>(
    (index, previousPageData) =>
      constructInfiniteScrollQuery({
        baseUrl:
          endpoints.v2_storefronts_id_dashboard_product_updates(storefront_id),
        query: "",
        previousPageData,
        index,
        pageSize: PAGE_SIZE,
      }),
    {
      onSuccess: (data) => {
        const updatedProducts = hasPermission([
          "view_products",
          "modify_products",
        ])
          ? data.flatMap(({ data }) => data)
          : [];
        setProductUpdates(updatedProducts);
      },
    }
  );

  return (
    <PageWrapper>
      <PageHeader>
        <HeaderLeft>
          <PageTitle>{t("Dashboard")}</PageTitle>
        </HeaderLeft>
        <HeaderRight>
          {roleIsSellerAdmin && (
            <TertiaryButtonMediumWithBellIcon
              onClick={() => history.push(`${adminPath}/broadcasts`)}
            >
              {t("Broadcasts")}
            </TertiaryButtonMediumWithBellIcon>
          )}
        </HeaderRight>
      </PageHeader>
      <FullWidthHorizontalSeparator />
      <DashboardParentWrapper>
        <SoftHeaderMedium style={{ marginBottom: spacingTokens.spacing_md }}>
          {date}
        </SoftHeaderMedium>
        <H3DarkText
          style={{ margin: `0 0 ${spacingTokens.spacing_4xl}` }}
        >{`${getGreeting}, ${user.firstname}`}</H3DarkText>
        <DisplayBroadcastMessages containerStyles={{ marginBottom: "22px" }} />
        {dashboardSummaryError ? (
          <ErrorPlaceholder
            message={t(
              "There was an error loading dashboard summary. Please try again later."
            )}
          />
        ) : isSummaryLoading ? (
          <DelayedSpinner />
        ) : dashboardSummary ? (
          <>
            <DashboardSummarySection
              style={{ marginBottom: spacingTokens.spacing_4xl }}
            >
              {dashboardSummaryArr.map(({ label, value }) => (
                <DashboardSummaryItem key={label}>
                  <H5 style={{ color: theme.navLinkColor }}>{label}</H5>
                  <H3DarkText
                    style={{ margin: `${spacingTokens.spacing_md} 0` }}
                  >
                    {dashboardSummary![value]}
                  </H3DarkText>
                </DashboardSummaryItem>
              ))}
            </DashboardSummarySection>
            {hasPermission(["delete_products", "modify_products"]) && (
              <DashboardSummarySection>
                {dashboardSummary.average_active_completion_score !== null && (
                  <DashboardSummaryItem>
                    <H5 style={{ color: theme.navLinkColor }}>
                      {t("Total Product Completion")}
                    </H5>
                    <H3DarkText
                      style={{ margin: `${spacingTokens.spacing_md} 0` }}
                    >
                      {dashboardSummary.average_active_completion_score}%
                    </H3DarkText>
                  </DashboardSummaryItem>
                )}
                {dashboardSummary.average_published_completion_score !==
                  null && (
                  <DashboardSummaryItem>
                    <H5 style={{ color: theme.navLinkColor }}>
                      {t("Published Product Completion")}
                    </H5>
                    <H3DarkText
                      style={{ margin: `${spacingTokens.spacing_md} 0` }}
                    >
                      {dashboardSummary.average_published_completion_score}%
                    </H3DarkText>
                  </DashboardSummaryItem>
                )}
                {dashboardSummary.average_active_mandatory_completion_score !==
                  null && (
                  <DashboardSummaryItem>
                    <H5 style={{ color: theme.navLinkColor }}>
                      {t("Total Mandatory Completion")}
                    </H5>
                    <H3DarkText
                      style={{ margin: `${spacingTokens.spacing_md} 0` }}
                    >
                      {
                        dashboardSummary.average_active_mandatory_completion_score
                      }
                      %
                    </H3DarkText>
                  </DashboardSummaryItem>
                )}
                {dashboardSummary.average_published_mandatory_completion_score !==
                  null && (
                  <DashboardSummaryItem>
                    <H5 style={{ color: theme.navLinkColor }}>
                      {t("Published Mandatory Completion")}
                    </H5>
                    <H3DarkText
                      style={{ margin: `${spacingTokens.spacing_md} 0` }}
                    >
                      {
                        dashboardSummary.average_published_mandatory_completion_score
                      }
                      %
                    </H3DarkText>
                  </DashboardSummaryItem>
                )}
              </DashboardSummarySection>
            )}
          </>
        ) : null}
        <DashboardUpdatesSection>
          <DashboardProductUpdates
            data={productUpdates}
            error={productUpdatesError}
            setSize={setProductUpdatesSize}
            size={productUpdatesSize}
          />
          <DashboardOtherUpdates
            data={otherUpdates}
            error={otherUpdatesError}
            setSize={setOtherUpdatesSize}
            size={otherUpdatesSize}
          />
        </DashboardUpdatesSection>
      </DashboardParentWrapper>
    </PageWrapper>
  );
};
